import dayjs from 'dayjs';
import { ReactNode } from 'react';
import { useTheme } from 'styled-components';

import { TasksCardChart } from './TasksCardChart';

import { getPath, useNavigation } from '@speeki/client-admin';
import { Skeleton, Tooltip, Typography } from '@speeki/global-ui-components';
import { IconManager } from '@speeki/icons';

import { CardBadge } from '@components/atoms/CardBadge/CardBadge';
import { CardLink } from '@components/atoms/CardLink/CardLink';
import { PlaceholderCard } from '@components/atoms/PlaceholderCard/PlaceholderCard';
import { StatisticsCardEmptyState } from '@components/molecules/StatisticsCards/components/StatisticsCardEmptyState/StatisticsCardEmptyState';
import { ActionWrapper } from '@components/molecules/StatisticsCards/StatisticsCards.styled';

import {
    Task_Status,
    Task_Type,
    TaskConnectionInput,
    TasksCardDocument,
} from '@graphql/generated/graphql';

import { useQuery } from '@apollo/client';

import {
    AdditionalStatistics,
    ContentWrapper,
    StatItem,
    StatsSection,
    TaskBadge,
    TaskChartWrapper,
    TaskHeader,
    TaskItem,
    TaskItemContent,
    TaskList,
    TasksCardWrapper,
    TaskStats,
    TasksTitleWrapper,
    WarnItem,
} from './TasksCard.styled';

export interface TasksCardProps {
    hidden?: boolean;
}

const DUE_DAY_WARN_DAYS = 7;

const statusIcons: Record<Task_Status, ReactNode> = {
    CLOSED: (
        <IconManager
            icon="SvgTickRounded24"
            size={24}
            themeColors={{
                default: {
                    'color-1': 'semantic.success',
                },
            }}
        />
    ),
    IN_PROGRESS: (
        <IconManager
            icon="SvgInProgressStatus24"
            size={24}
            themeColors={{
                default: {
                    'color-1': 'chart.blue500',
                },
            }}
        />
    ),
    OPEN: (
        <IconManager
            icon={'SvgOpenStatus24'}
            size={24}
            themeColors={{
                default: {
                    'color-1': 'text.assistive',
                },
            }}
        />
    ),
};

const typeIcons: Record<Exclude<Task_Type, 'METRIC'>, ReactNode> = {
    DISCLOSURE: <IconManager icon="Svg2DDAndD24" size={24} />,
    PROFILE: <IconManager icon="SvgDatabase24" size={24} />,
    SURVEY: <IconManager icon="Svg2DSurvey24" size={24} />,
};

const commonTasksInput: TaskConnectionInput = {
    paging: {
        limit: 3,
        offset: 1,
    },
    sorting: [
        {
            direction: 'ASC',
            field: 'DUE_DATE',
        },
    ],
};

//TODO: refactor this component, split into smaller components, move logic to hook
export const TasksCard = ({ hidden }: TasksCardProps) => {
    const { navigate } = useNavigation();

    const { palette } = useTheme();

    const { data, loading } = useQuery(TasksCardDocument, {
        skip: hidden,
        variables: {
            completedInput: {
                ...commonTasksInput,
                filtering: {
                    assignmentMode: 'ASSIGNED_TO_ME',
                    statuses: ['CLOSED'],
                },
            },
            inProgressInput: {
                ...commonTasksInput,
                filtering: {
                    assignmentMode: 'ASSIGNED_TO_ME',
                    statuses: ['IN_PROGRESS'],
                },
            },
            notStartedInput: {
                ...commonTasksInput,
                filtering: {
                    assignmentMode: 'ASSIGNED_TO_ME',
                    statuses: ['OPEN'],
                },
            },
        },
    });

    const allTasksData = [
        ...(data?.completedTasks.nodes || []),
        ...(data?.inProgressTasks.nodes || []),
        ...(data?.notStartedTasks.nodes || []),
    ];

    const tasksDataSliced = allTasksData.slice(0, 3);

    const tasksStatistics = data?.inProgressTasks.statistics &&
        data?.completedTasks.statistics &&
        data?.notStartedTasks.statistics && {
            completed: {
                newCount: data.completedTasks.statistics.newCount,
                totalCount: data.completedTasks.statistics.totalCount,
            },
            inProgress: {
                newCount: data.inProgressTasks.statistics.newCount,
                overdueCount: data.inProgressTasks.statistics.overdueCount,
                totalCount: data.inProgressTasks.statistics.totalCount,
                upcomingCount: data.inProgressTasks.statistics.upcomingCount,
            },
            notStarted: {
                newCount: data.notStartedTasks.statistics.newCount,
                overdueCount: data.notStartedTasks.statistics.overdueCount,
                totalCount: data.notStartedTasks.statistics.totalCount,
                upcomingCount: data.notStartedTasks.statistics.upcomingCount,
            },
            totalCount:
                data.inProgressTasks.statistics.totalCount +
                data.completedTasks.statistics.totalCount +
                data.notStartedTasks.statistics.totalCount,
        };

    const { completed, inProgress, notStarted } = tasksStatistics || {};
    const newTasksCount =
        (inProgress?.newCount || 0) +
        (completed?.newCount || 0) +
        (notStarted?.newCount || 0);

    return (
        <PlaceholderCard gridSize={2} hidden={hidden}>
            <TasksCardWrapper>
                <TaskHeader>
                    <TasksTitleWrapper>
                        <PlaceholderCard.Title>
                            Your tasks
                        </PlaceholderCard.Title>
                        {!!newTasksCount && (
                            <TaskBadge>
                                {newTasksCount} NEW TASK
                                {newTasksCount > 1 ? 'S' : ''}
                            </TaskBadge>
                        )}
                    </TasksTitleWrapper>
                    <PlaceholderCard.Description>
                        Keep track of your tasks.
                    </PlaceholderCard.Description>
                </TaskHeader>

                {loading && (
                    <ContentWrapper>
                        <TaskList>
                            {Array.from({ length: 3 }).map((_, index) => (
                                <Skeleton
                                    height={48}
                                    key={index}
                                    width="100%"
                                />
                            ))}
                        </TaskList>
                        <Skeleton height={140} width="100%" />
                    </ContentWrapper>
                )}

                {!loading && (
                    <ContentWrapper>
                        {tasksDataSliced.length ? (
                            <TaskList>
                                {tasksDataSliced.map((task) => {
                                    const differenceToDueDate = dayjs(
                                        task.dueDate,
                                    ).diff(new Date(), 'days');

                                    let expiredIcon: ReactNode | null = null;
                                    if (
                                        task.status !== 'CLOSED' &&
                                        differenceToDueDate < DUE_DAY_WARN_DAYS
                                    )
                                        expiredIcon = (
                                            <Tooltip
                                                placement={'top'}
                                                removePortal
                                                testId="TshJIdb"
                                                text={
                                                    differenceToDueDate < 0
                                                        ? 'The due date for completing this task has passed'
                                                        : 'The due date for this task is coming up'
                                                }
                                            >
                                                <IconManager
                                                    icon={'SvgAlert24'}
                                                    size={24}
                                                    themeColors={{
                                                        default: {
                                                            'color-1':
                                                                'semantic.error',
                                                        },
                                                    }}
                                                />
                                            </Tooltip>
                                        );

                                    return (
                                        <TaskItem
                                            href={getPath({
                                                params: { id: task.uuid },
                                                path: '/tasks/:id',
                                            })}
                                            key={task.uuid}
                                        >
                                            <TaskItemContent>
                                                {
                                                    typeIcons[
                                                        task.type as Exclude<
                                                            Task_Type,
                                                            'METRIC'
                                                        >
                                                    ]
                                                }
                                                <Typography.TextBody_14_20 fontWeight="bold">
                                                    {task.title}
                                                </Typography.TextBody_14_20>
                                                {task.isNew && (
                                                    <CardBadge>NEW</CardBadge>
                                                )}
                                                {expiredIcon ||
                                                    statusIcons[task.status]}
                                            </TaskItemContent>
                                            <IconManager
                                                icon="SvgChevronRight24"
                                                size={24}
                                                themeColors={{
                                                    default: {
                                                        'color-1':
                                                            'tertiary.default',
                                                    },
                                                }}
                                            />
                                        </TaskItem>
                                    );
                                })}
                            </TaskList>
                        ) : (
                            <StatisticsCardEmptyState
                                action={{
                                    label: 'create task',
                                    onClick: () =>
                                        navigate({ path: '/tasks/create' }),
                                }}
                            >
                                There are no tasks yet.
                            </StatisticsCardEmptyState>
                        )}

                        {tasksStatistics && (
                            <TaskChartWrapper>
                                <TasksCardChart
                                    allTasks={allTasksData}
                                    statistics={tasksStatistics}
                                    totalTasks={
                                        tasksStatistics?.totalCount || 0
                                    }
                                />

                                {!!tasksStatistics?.totalCount && (
                                    <TaskStats>
                                        {!!notStarted?.totalCount &&
                                            notStarted.totalCount > 0 && (
                                                <StatsSection>
                                                    <StatItem
                                                        color={
                                                            palette.neutral.gray
                                                        }
                                                    >
                                                        <Typography.TextBody_12_20>
                                                            not started:{' '}
                                                            <b>
                                                                {
                                                                    notStarted.totalCount
                                                                }
                                                            </b>
                                                        </Typography.TextBody_12_20>
                                                    </StatItem>

                                                    {(!!notStarted.overdueCount ||
                                                        !!notStarted.upcomingCount) && (
                                                        <AdditionalStatistics>
                                                            {!!notStarted.overdueCount && (
                                                                <WarnItem>
                                                                    <IconManager
                                                                        icon={
                                                                            'SvgAlert24'
                                                                        }
                                                                        size={
                                                                            16
                                                                        }
                                                                        themeColors={{
                                                                            default:
                                                                                {
                                                                                    'color-1':
                                                                                        'semantic.error',
                                                                                },
                                                                        }}
                                                                    />
                                                                    <Typography.TextBody_12_20>
                                                                        overdue:{' '}
                                                                        <b>
                                                                            {
                                                                                notStarted.overdueCount
                                                                            }
                                                                        </b>
                                                                    </Typography.TextBody_12_20>
                                                                </WarnItem>
                                                            )}
                                                            {!!notStarted.upcomingCount && (
                                                                <WarnItem>
                                                                    <IconManager
                                                                        icon={
                                                                            'SvgAlert24'
                                                                        }
                                                                        size={
                                                                            16
                                                                        }
                                                                        themeColors={{
                                                                            default:
                                                                                {
                                                                                    'color-1':
                                                                                        'priority.medium',
                                                                                },
                                                                        }}
                                                                    />
                                                                    <Typography.TextBody_12_20>
                                                                        due
                                                                        soon:{' '}
                                                                        <b>
                                                                            {
                                                                                notStarted.upcomingCount
                                                                            }
                                                                        </b>
                                                                    </Typography.TextBody_12_20>
                                                                </WarnItem>
                                                            )}
                                                        </AdditionalStatistics>
                                                    )}
                                                </StatsSection>
                                            )}

                                        {!!inProgress?.totalCount &&
                                            inProgress.totalCount > 0 && (
                                                <StatsSection>
                                                    <StatItem
                                                        color={
                                                            palette.chart
                                                                .blue500
                                                        }
                                                    >
                                                        <Typography.TextBody_12_20>
                                                            in progress:{' '}
                                                            <b>
                                                                {
                                                                    inProgress.totalCount
                                                                }
                                                            </b>
                                                        </Typography.TextBody_12_20>
                                                    </StatItem>

                                                    {(!!inProgress.overdueCount ||
                                                        !!inProgress.upcomingCount) && (
                                                        <AdditionalStatistics>
                                                            {!!inProgress.overdueCount && (
                                                                <WarnItem>
                                                                    <IconManager
                                                                        icon={
                                                                            'SvgAlert24'
                                                                        }
                                                                        size={
                                                                            16
                                                                        }
                                                                        themeColors={{
                                                                            default:
                                                                                {
                                                                                    'color-1':
                                                                                        'semantic.error',
                                                                                },
                                                                        }}
                                                                    />
                                                                    <Typography.TextBody_12_20>
                                                                        overdue:{' '}
                                                                        <b>
                                                                            {
                                                                                inProgress.overdueCount
                                                                            }
                                                                        </b>
                                                                    </Typography.TextBody_12_20>
                                                                </WarnItem>
                                                            )}
                                                            {!!inProgress.upcomingCount && (
                                                                <WarnItem>
                                                                    <IconManager
                                                                        icon={
                                                                            'SvgAlert24'
                                                                        }
                                                                        size={
                                                                            16
                                                                        }
                                                                        themeColors={{
                                                                            default:
                                                                                {
                                                                                    'color-1':
                                                                                        'priority.medium',
                                                                                },
                                                                        }}
                                                                    />
                                                                    <Typography.TextBody_12_20>
                                                                        due
                                                                        soon:{' '}
                                                                        <b>
                                                                            {
                                                                                inProgress.upcomingCount
                                                                            }
                                                                        </b>
                                                                    </Typography.TextBody_12_20>
                                                                </WarnItem>
                                                            )}
                                                        </AdditionalStatistics>
                                                    )}
                                                </StatsSection>
                                            )}

                                        {!!completed?.totalCount &&
                                            completed.totalCount > 0 && (
                                                <StatsSection>
                                                    <StatItem
                                                        color={
                                                            palette.semantic
                                                                .success
                                                        }
                                                    >
                                                        <Typography.TextBody_12_20>
                                                            completed:{' '}
                                                            <b>
                                                                {
                                                                    completed.totalCount
                                                                }
                                                            </b>
                                                        </Typography.TextBody_12_20>
                                                    </StatItem>
                                                </StatsSection>
                                            )}
                                    </TaskStats>
                                )}
                            </TaskChartWrapper>
                        )}
                    </ContentWrapper>
                )}

                <ActionWrapper>
                    <CardLink href={getPath({ path: '/tasks' })}>
                        Go to Tasks
                    </CardLink>
                </ActionWrapper>
            </TasksCardWrapper>
        </PlaceholderCard>
    );
};
